import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {catchError, EMPTY, forkJoin, Observable} from 'rxjs';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {Club, Lottery, TixxEvent, TixxEventType} from '~core/models';
import {DefaultNotificationService, StorageService, TixxSelectService} from '~core/services';
import {TixxSelectTranslationsService} from '~shared/translations';
import {NotificationType} from '~core/enum';
import {SpinnerService} from "~shared/components/buttons/spinner/spinner.service";

/**
 * Form component for creating a new tixx event
 */
@UntilDestroy()
@Component({
  selector: 'app-create-tixx-select',
  templateUrl: './create-tixx-select.component.html'
})

export class CreateTixxSelectComponent implements OnInit {
  public selectedEvent: TixxEvent;
  public availableTixxEvents: Array<TixxEvent>;
  public tixxEventTypes: Array<TixxEventType>;
  public createOrderListForm: UntypedFormGroup;
  public selectedClub: Club;
  public spinner: string = "newTixxSelect";

  @Output() closeButton: EventEmitter<any> = new EventEmitter<any>(null);

  constructor(
    public flTranslations: FormLabelTranslationService,
    public btnTranslations: ButtonsTranslations,
    private router: Router,
    private storage: StorageService,
    private formBuilder: UntypedFormBuilder,
    private tixxSelectService: TixxSelectService,
    private notificationService: DefaultNotificationService,
    private tixxSelectTranslations: TixxSelectTranslationsService,
    public spinnerService: SpinnerService,
  ) {
  }

  ngOnInit(): void {
    this.selectedClub = this.storage.selectedClubValue;
    this.fetchEvents();
  }

  /**
   * fetches all the available events.
   */
  public fetchEvents(): void {
    const calls: Array<undefined | Observable<any>> = [
      this.tixxSelectService.getTixxEvents(this.selectedClub.id).pipe(untilDestroyed(this), catchError(error => this.handleError(error))),
      this.tixxSelectService.getTixxEventTypes(this.selectedClub.id).pipe(untilDestroyed(this))
    ];

    forkJoin(calls).subscribe(([events, types]) => {
      this.availableTixxEvents = events as Array<TixxEvent>;
      this.tixxEventTypes = types as Array<TixxEventType>;
      this.spinnerService.hide(this.spinner);
    });
  }

  private handleError(error) {
    if (error.status === 500) {
      this.notificationService.showNotification({
        type: NotificationType.ALERT,
        message: this.tixxSelectTranslations.i18nErrorInvalidCredentials
      });
    } else {
      this.notificationService.showNotification({
        type: NotificationType.ALERT,
        message: this.tixxSelectTranslations.i18nErrorFetchingEvents
      });
    }
    this.spinnerService.hide(this.spinner);
    this.closeModal();
    return EMPTY;
  }

  /**
   * Fill the event form.
   */
  public fillOrderListForm(event: TixxEvent): void {
    this.createOrderListForm = this.formBuilder.group({
      tixxId: [{value: event.id.toString(), disabled: true}],
      type: [{value: this.tixxEventTypes[0].eventTypeId, disabled: false}, [Validators.required]],
      name: [{value: event.name, disabled: true}],
      date: [{value: event.date.dateIso, disabled: true}],
      homeTeam: [{value: event.homePerformer, disabled: true}],
      homeTeamLogoUrl: [{value: event.homePerformerLogoUrl, disabled: true}],
      guestTeam: [{value: event.guestPerformer, disabled: true}],
      guestTeamLogoUrl: [{value: event.guestPerformerLogoUrl, disabled: true}],
      competition: [{value: event.competitionName, disabled: true}],
      venue: [{value: event.location, disabled: true}]
    });
  }

  /**
   * Selects the emitted tixx event and displays the full details of the event
   */
  public selectTixxEvent(id: any): void {
    if (id === 'null') {
      this.selectedEvent = null;
      this.createOrderListForm.reset();
      return;
    }
    const event = this.availableTixxEvents.find(el => el.id === Number(id));
    this.fillOrderListForm(event);
    this.selectedEvent = event;
  }

  /**
   * Submit the form to the backend
   */
  public submitTixxEvent(): void {
    this.createOrderListForm.controls['date'].patchValue(new Date(this.createOrderListForm.controls['date'].value));
    const form = {
      organizer: `/api/l/organizers/${this.selectedClub.id}`,
      event: this.createOrderListForm.getRawValue()
    };
    this.tixxSelectService.postLottery(form).pipe(untilDestroyed(this))
      .subscribe({
        next: (lottery: Lottery) => {
          this.notificationService.showNotification({
            type: NotificationType.SUCCESS,
            message: this.tixxSelectTranslations.i18nSuccessCreateLottery
          });
          this.tixxSelectService.setSelectedLottery(lottery);
          this.router.navigate(['club', this.selectedClub.id, 'tixx-select-overview', lottery.id]);
        },
        error: error => {
          this.notificationService.showNotification({
            type: NotificationType.ALERT,
            message: this.tixxSelectTranslations.i18nErrorCreateLottery
          });
        }
      });
  }

  /**
   * Close the modal
   */
  public closeModal(): void {
    this.closeButton.emit('close');
  }
}
